/**
 * Download DOCX Menu Component
 * Provides options to download DOCX with or without company template
 */

import { useTranslation } from "react-i18next"

import { Download, FileText, FileType } from "@/components/icons"
import { buttonVariants } from "@/components/ui/button-variants"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

type DownloadDocxMenuProps = {
  onDownload: (useTemplate: boolean) => void
  disabled?: boolean
  hasTemplate?: boolean
}

export function DownloadDocxMenu({
  onDownload,
  disabled = false,
  hasTemplate = false,
}: DownloadDocxMenuProps) {
  const { t } = useTranslation()
  const label = `${t("answer.download_as")} docx`

  // If no template is configured, just show a simple button
  if (!hasTemplate) {
    return (
      <Tooltip>
        <TooltipTrigger
          aria-label={label}
          data-testid="download-docx-button"
          disabled={disabled}
          className={cn(
            buttonVariants({
              size: "icon",
              variant: "icon",
              className: "size-7",
            })
          )}
          onClick={() => onDownload(false)}
        >
          <Download className="size-5" />
        </TooltipTrigger>
        <TooltipContent className="max-w-[250px]" side="bottom" sideOffset={5}>
          <p>{label}</p>
        </TooltipContent>
      </Tooltip>
    )
  }

  // If template is available, show dropdown with options
  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger
            aria-label={label}
            data-testid="download-docx-menu-trigger"
            disabled={disabled}
            className={cn(
              buttonVariants({
                size: "icon",
                variant: "icon",
                className: "size-7",
              })
            )}
          >
            <Download className="size-5" />
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent className="max-w-[250px]" side="bottom" sideOffset={5}>
          <p>{label}</p>
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          className="cursor-pointer"
          data-testid="download-docx-standard"
          onClick={() => onDownload(false)}
        >
          <FileText className="mr-2 h-4 w-4" />
          <span>{t("answer.download_docx_standard", "Save as DOCX (Standard)")}</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          className="cursor-pointer"
          data-testid="download-docx-template"
          onClick={() => onDownload(true)}
        >
          <FileType className="mr-2 h-4 w-4" />
          <span>{t("answer.download_docx_template", "Save as DOCX (Company Template)")}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
